import type { CreateAppointmentTypeDto } from '@shared/core/appointment-types'
import { extractErrorMessage } from '@shared/util'
import type { AxiosError } from 'axios'
import { useState } from 'react'
import type { BookingQuestionsEditorController } from 'src/modules/user/components/booking-questions-editor'
import {
  isQuestionsDirty,
  mergeSeededMessageId,
  QUESTIONS_CONFLICT_MESSAGE,
  toSaveQuestionsPayload,
  type QuestionsEditorState,
} from 'src/modules/user/components/booking-questions-editor/booking-questions-editor.domain'
import {
  useCreateAppointmentType,
  useSaveBookingQuestions,
  useUpdateAppointmentType,
} from 'src/modules/user/hooks/use-appointment-types'
import { SAVE_APPOINTMENT_TYPE_ERROR_FALLBACK } from 'src/modules/user/utils/appointment-type-form'

interface SaveTarget {
  id: string
  questions: QuestionsEditorState
  revision: number
}

interface CreatedInModal {
  id: string
  fieldsJson: string
}

/** Settings first, then the questions PUT only when they changed (design D33); a created type is never re-created. */
export function useAppointmentTypeSave(
  appointmentTypeId: string | undefined,
  editor: BookingQuestionsEditorController,
) {
  const createAppointmentType = useCreateAppointmentType()
  const updateAppointmentType = useUpdateAppointmentType()
  const saveBookingQuestions = useSaveBookingQuestions()
  const [created, setCreated] = useState<CreatedInModal | null>(null)

  const currentTarget = (id: string): SaveTarget => ({ id, questions: editor.state, revision: editor.expectedRevision })

  const createType = async (fields: CreateAppointmentTypeDto): Promise<SaveTarget> => {
    const createdType = await createAppointmentType.mutateAsync(fields)
    const questions = mergeSeededMessageId(editor.state, createdType?.booking_questions ?? [])
    const revision = createdType?.booking_questions_revision ?? 1
    editor.replaceState(questions)
    editor.setExpectedRevision(revision)
    setCreated({ id: createdType?.id, fieldsJson: JSON.stringify(fields) })
    return { id: createdType?.id, questions, revision }
  }

  const saveSettings = async (fields: CreateAppointmentTypeDto): Promise<SaveTarget> => {
    if (appointmentTypeId) {
      await updateAppointmentType.mutateAsync({ id: appointmentTypeId, data: fields })
      return currentTarget(appointmentTypeId)
    }
    if (!created) return createType(fields)
    const fieldsJson = JSON.stringify(fields)
    if (fieldsJson !== created.fieldsJson) {
      await updateAppointmentType.mutateAsync({ id: created.id, data: fields })
      setCreated({ id: created.id, fieldsJson })
    }
    return currentTarget(created.id)
  }

  /** Resolves to the message to show, or null when everything saved. */
  const save = async (fields: CreateAppointmentTypeDto): Promise<string | null> => {
    try {
      const target = await saveSettings(fields)
      if (!isQuestionsDirty(target.questions)) return null
      const questions = toSaveQuestionsPayload(target.questions.questions)
      await saveBookingQuestions.mutateAsync({ id: target.id, data: { expected_revision: target.revision, questions } })
      return null
    } catch (error) {
      console.error('Failed to save appointment type:', error)
      return describeSaveError(error)
    }
  }

  return {
    save,
    createdTypeId: created?.id,
    isSaving: createAppointmentType.isLoading || updateAppointmentType.isLoading || saveBookingQuestions.isLoading,
  }
}

function describeSaveError(error: unknown): string {
  if ((error as AxiosError | undefined)?.response?.status === 409) return QUESTIONS_CONFLICT_MESSAGE
  return extractErrorMessage(error, SAVE_APPOINTMENT_TYPE_ERROR_FALLBACK)
}
